import React, { useState, useEffect } from 'react';
import { FiBriefcase, FiBook, FiCode, FiDownload } from 'react-icons/fi';
import DocumentTitle from "../../components/ui/DocumentTitle";
import { cvService } from "../../services/cvService";
import { skillsService } from "../../services/skillsService";
import { formatCvPeriod } from "../../utils/dateFormat";
import "./Resume.css";

function Resume() {
  const [experiences, setExperiences] = useState({ work: [], education: [] });
  const [skills, setSkills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [cvResponse, skillsData] = await Promise.all([
          cvService.getAll(),
          skillsService.getSkillNames()
        ]);

        const dataArray = cvResponse.Lines || [];
        
        // Fonction de traitement : Filtrage + Mapping + Tri Chronologique
        const processItems = (type) => {
          // Clé chronologique en "nombre de mois" pour trier même sans précision mensuelle
          const startKey = (cv) => Number(cv.start_year) * 12 + (Number(cv.start_month) || 0);
          const endKey = (cv) => cv.is_ongoing
            ? Infinity
            : Number(cv.end_year) * 12 + (Number(cv.end_month) || 0);

          return dataArray
            .filter(item => item.cv.type === type)
            .map(item => ({
              id: item.id,
              startKey: startKey(item.cv),
              endKey: endKey(item.cv),
              isOngoing: Boolean(item.cv.is_ongoing),
              date: formatCvPeriod(item.cv),
              title: item.cv.title,
              organization: item.cv.organization,
              desc: item.cv.description
            }))
            // Tri : les postes en cours d'abord, puis date de fin la plus récente, puis date de début
            .sort((a, b) => {
              if (b.endKey !== a.endKey) {
                return b.endKey - a.endKey;
              }
              return b.startKey - a.startKey;
            });
        };

        setExperiences({ 
          work: processItems('experience'), 
          education: processItems('diplome') 
        });
        setSkills(skillsData);

      } catch (err) {
        console.error("Erreur de synchronisation CV:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Composant interne pour générer une timeline avec ligne continue
  const renderTimeline = (items) => (
    <div className="resume-list">
      {items.map((exp) => (
        <article key={exp.id} className={`resume-item${exp.isOngoing ? ' resume-item--ongoing' : ''}`}>
          <span className="date">
            {exp.date}
            {exp.isOngoing && <span className="ongoing-badge">En cours</span>}
          </span>
          <h3 className="title">{exp.title}</h3>
          <span className="organization">{exp.organization}</span>
          <p className="desc">{exp.desc}</p>
        </article>
      ))}
    </div>
  );

  return (
    <div className="resume-page">
      <DocumentTitle title="CV" />

      <div className="container">
        <div className="resume-grid">

          {/* SIDEBAR : Compétences et bouton de téléchargement */}
          <aside className="resume-left">
            <section className="resume-section">
              <h2 className="resume-section-title"><FiCode /> Compétences Techniques</h2>
              <div className="skills-grid">
                {skills.map(skill => (
                  <div key={skill} className="skill-chip">
                    <p>{skill}</p>
                  </div>
                ))}
              </div>
            </section>

            <a href="/Theophile-Yvars-CV.pdf" className="resume-btn" download>
              <FiDownload /> Télécharger le CV
            </a>
          </aside>

          {/* CONTENU PRINCIPAL : Expériences triées par sections */}
          <main className="resume-right">

            {/* Navigation rapide entre les sections */}
            <nav className="resume-quicknav" aria-label="Navigation rapide du CV">
              <a href="#experience" className="resume-quicknav-link">
                <FiBriefcase /> Expérience Professionnelle
              </a>
              <a href="#formation" className="resume-quicknav-link">
                <FiBook /> Formation
              </a>
            </nav>

            {/* Section Expérience Professionnelle */}
            <div className="resume-group" id="experience">
              <h2 className="page-title"><FiBriefcase /> Expérience Professionnelle</h2>
              {isLoading ? (
                <p className="loading-state">Synchronisation...</p>
              ) : renderTimeline(experiences.work)}
            </div>

            {/* Section Éducation / Diplômes */}
            <div className="resume-group resume-group--formation" id="formation" style={{ marginTop: '100px' }}>
              <h2 className="page-title"><FiBook /> Formation</h2>
              {isLoading ? (
                <p className="loading-state">Synchronisation...</p>
              ) : renderTimeline(experiences.education)}
            </div>

          </main>

        </div>
      </div>
    </div>
  );
}

export default Resume;